import type { Model, StreamEvent, ToolCall } from '../agents/types';
import type { ModelProviderAdapter, StreamRequest } from './ModelProviderAdapter';
import { shortId } from '../utils';
import { runTool } from '../tools/runTool';

const MODELS: Model[] = [
  { id: 'mock-default', label: 'Mock', providerId: 'mock', description: 'In-process canned stream' },
];

interface ToolSample {
  name: string;
  input: unknown;
  /** if provided, used when the registered tool has no executor */
  fallbackOutput?: unknown;
}

const TOOL_SAMPLES: ToolSample[] = [
  // real tools — actually run
  { name: 'now', input: {} },
  {
    name: 'fetch_url',
    input: { url: 'https://api.github.com/repos/Empiish/Xoro' },
  },
  // stub tools — fall back to canned output
  {
    name: 'grep',
    input: { pattern: 'function\\s+main' },
    fallbackOutput:
      'src/index.ts:14:export function main() {\nsrc/server.ts:9:async function main() {',
  },
  {
    name: 'read_file',
    input: { path: 'src/lib/store/sessionStore.ts' },
    fallbackOutput:
      "// excerpt\nexport const useSessionStore = create<SessionStore>()(persist((set, get) => ({\n  sessions: MOCK_SESSIONS,\n  ...",
  },
];

const REASONING = [
  'I need to understand the user request and plan the smallest correct change.',
  'Let me check what context already exists before suggesting modifications.',
  'A small, well-scoped diff is preferable to broad refactors.',
];

const RESPONSES = [
  `Here's a clean approach.

### Outline
1. Identify the smallest unit that owns this concern.
2. Add the new behavior behind a feature flag for rollout safety.
3. Cover with a focused unit test before shipping.

\`\`\`ts
export function shouldEnable(scope: Scope, flag: string): boolean {
  return scope.flags?.[flag] === true;
}
\`\`\`

Let me know if you want me to wire this into the call site directly.`,

  `Quick take:

- The current pattern is fine for one-off use, but it leaks responsibility across two modules.
- Consider extracting a small \`useStreamingChat\` hook so the Composer can stay dumb.
- Persistence already covers this through Zustand — no extra plumbing needed.

Happy to refactor in place if you want a draft.`,

  `Plan:

| Step | What | Why |
| --- | --- | --- |
| 1 | Add adapter interface | swap providers without UI changes |
| 2 | Wire mock first | iterate without API costs |
| 3 | Real provider behind env flag | gate on \`ANTHROPIC_API_KEY\` |

I'll start with step 1 and post a diff.`,
];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

class AbortError extends Error {
  override name = 'AbortError';
  constructor() {
    super('Aborted');
  }
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new AbortError());
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(new AbortError());
    };
    signal?.addEventListener('abort', onAbort);
  });
}

function chunksFor(text: string, granularity: 'word' | 'short' = 'word'): string[] {
  if (granularity === 'short') {
    return text.match(/.{1,3}/g) ?? [text];
  }
  // split by whitespace but keep separators so re-join produces the original
  return text.split(/(\s+)/).filter(Boolean);
}

export const MockAdapter: ModelProviderAdapter = {
  id: 'mock',
  label: 'Mock',
  configured: true,
  listModels: () => MODELS,

  async *stream(req: StreamRequest): AsyncIterable<StreamEvent> {
    const { signal, effort } = req;
    const messageId = shortId('msg');
    const effortMultiplier =
      effort === 'low' ? 0.5 : effort === 'high' ? 1.4 : effort === 'extra_high' ? 1.8 : effort === 'max' ? 2.2 : 1;

    yield { type: 'message-start', messageId, role: 'assistant' };

    // 1) reasoning phase
    yield { type: 'status', messageId, status: 'thinking' };
    const reasoningText = REASONING.slice(0, Math.max(1, Math.round(2 * effortMultiplier))).join(' ');
    for (const ch of chunksFor(reasoningText, 'short')) {
      await delay(15, signal);
      yield { type: 'reasoning-delta', messageId, delta: ch };
    }

    // 2) tool call phase (50% chance, scaled by effort)
    if (Math.random() < Math.min(0.85, 0.4 + effortMultiplier * 0.2)) {
      yield { type: 'status', messageId, status: 'tool_calling' };
      const sample = pick(TOOL_SAMPLES);
      const tcId = shortId('tc');
      const startedAt = Date.now();
      const toolCall: ToolCall = {
        id: tcId,
        name: sample.name,
        status: 'running',
        input: sample.input,
        startedAt,
      };
      yield { type: 'tool-call', messageId, toolCall };

      // try to actually run the tool; fall back to canned sample
      const result = await runTool(sample.name, sample.input, signal);
      const ranReal = result.ok && result.output !== undefined;
      yield {
        type: 'tool-update',
        messageId,
        toolCallId: tcId,
        patch: ranReal
          ? { status: 'success', output: result.output, endedAt: Date.now() }
          : sample.fallbackOutput !== undefined
          ? { status: 'success', output: sample.fallbackOutput, endedAt: Date.now() }
          : { status: 'error', error: result.error ?? 'Tool failed.', endedAt: Date.now() },
      };
    }

    // 3) text response phase
    yield { type: 'status', messageId, status: 'streaming' };
    const text = pick(RESPONSES);
    for (const tok of chunksFor(text, 'word')) {
      await delay(20 + Math.random() * 30, signal);
      yield { type: 'text-delta', messageId, delta: tok };
    }

    // 4) end
    yield {
      type: 'message-end',
      messageId,
      trailingStatus: `Mock · ${Math.round((Date.now() - 0) % 1000)}ms`,
    };
  },
};
